import { CdkDrag, CdkDragDrop, CdkDropList } from "@angular/cdk/drag-drop";
import { ChangeDetectionStrategy, Component, input } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputOutline, TextareaComponent, ToggleComponent } from "@sxm-content-creators/tools-ds";
import { ImageUploadComponent } from "../../../../components/image-upload/image-upload.component";
import { BadgeResponse, ThemeAssetUploadResponse } from "../../models/theme.model";

/** Fixed number of response template slots — all shown, all optional. */
export const MAX_RESPONSES = 10;
/** Max length of a response's short internal name. */
export const RESPONSE_NAME_MAX = 25;
/** Max length of a response's text body. */
export const RESPONSE_TEXT_MAX = 1000;

/**
 * One response card's form. `image` holds the uploaded asset key (`^public/.+`) and `imageUrl`
 * the preview URL; `displayOrder` is NOT a control — it's derived from card order on submit.
 * `responseIsActive` defaults off: a card goes live only when the user turns it on. A card whose
 * toggle can't enable is forced inactive at save. Existing responses keep their saved value.
 */
export type ResponseRowGroup = FormGroup<{
    id: FormControl<string>;
    name: FormControl<string>;
    image: FormControl<string>;
    imageUrl: FormControl<string>;
    response: FormControl<string>;
    responseIsActive: FormControl<boolean>;
}>;

/** A response row's raw value — what the rules below read, and what the parent maps to a badge. */
export type ResponseRowValue = ReturnType<ResponseRowGroup["getRawValue"]>;

/** Whether a row has response text or a badge image — what the backend requires of every badge. */
function rowHasContent(row: ResponseRowValue): boolean {
    return !!row.response.trim() || !!row.image;
}

/**
 * Whether a card is complete enough to go live: a short name, plus response text or a badge
 * image. It gates the card's "Response is active" toggle and, via `rowWouldBeActive`, the flag
 * the parent form sends.
 */
export function rowCanActivate(row: ResponseRowValue): boolean {
    return !!row.name.trim() && rowHasContent(row);
}

/** Whether a row would be sent as an active badge: complete enough, and toggled on. */
export function rowWouldBeActive(row: ResponseRowValue): boolean {
    return rowCanActivate(row) && row.responseIsActive;
}

/**
 * Whether a row carries content but no short name. The backend requires a name on every badge it
 * receives (`BadgeConfigPayload.name` is `@NotBlank`), active or not, so this fails the whole save.
 */
export function rowNeedsName(row: ResponseRowValue): boolean {
    return !row.name.trim() && rowHasContent(row);
}

/**
 * Whether a row has a short name and nothing else. The backend requires response text or an image
 * on every badge (`BadgeConfigPayload.isImageOrResponsePresent`), active or not, so this also
 * fails the whole save.
 */
export function rowNeedsContent(row: ResponseRowValue): boolean {
    return !!row.name.trim() && !rowHasContent(row);
}

/** Builds a response row, optionally pre-filled from an existing badge (edit mode). */
export function createResponseRow(fb: FormBuilder, badge?: BadgeResponse): ResponseRowGroup {
    return fb.nonNullable.group({
        id: fb.nonNullable.control(badge?.id ?? ""),
        name: fb.nonNullable.control(badge?.name ?? "", [Validators.maxLength(RESPONSE_NAME_MAX)]),
        image: fb.nonNullable.control(badge?.image ?? ""),
        imageUrl: fb.nonNullable.control(badge?.imageUrl ?? ""),
        response: fb.nonNullable.control(badge?.response ?? "", [Validators.maxLength(RESPONSE_TEXT_MAX)]),
        responseIsActive: fb.nonNullable.control(badge?.responseIsActive ?? false)
    });
}

/**
 * "Response Templates" section of the theme form (Responses tab). Renders all
 * `MAX_RESPONSES` cards in a fixed 2-per-row grid — each a short name, an image, and response
 * text. A card can be left entirely empty; one with any content needs a short name plus response
 * text or an image to save. Cards are drag-reorderable (order is the display order, derived on
 * submit by the parent). Every card shows a "Response is active" toggle, disabled (and reading
 * off) until the card is complete enough to save.
 */
@Component({
    selector: "app-theme-responses",
    templateUrl: "./theme-responses.component.html",
    styleUrl: "./theme-responses.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        InputOutline,
        TextareaComponent,
        ImageUploadComponent,
        ToggleComponent,
        CdkDropList,
        CdkDrag
    ]
})
export class ThemeResponsesComponent {
    /** Parent-owned array of response cards; this component only reads/mutates it. */
    readonly responses = input.required<FormArray<ResponseRowGroup>>();

    /** Stores the uploaded asset key + preview URL on the card. */
    onImageUploaded(row: ResponseRowGroup, upload: ThemeAssetUploadResponse): void {
        row.controls.image.setValue(upload.key);
        row.controls.imageUrl.setValue(upload.url);
    }

    /** Reorders the FormArray on drop; the CDK animates the move, order is persisted on Save. */
    onDrop(event: CdkDragDrop<ResponseRowGroup[]>): void {
        if (event.previousIndex === event.currentIndex) return;
        const responses = this.responses();
        const moved = responses.at(event.previousIndex);
        responses.removeAt(event.previousIndex);
        responses.insert(event.currentIndex, moved);
    }

    /**
     * Whether the card's "Response is active" toggle is enabled. Until it is, the toggle reads off
     * while the row's stored value is left alone, so a card that qualifies again keeps whatever the
     * user last chose.
     */
    canActivate(row: ResponseRowGroup): boolean {
        return rowCanActivate(row.getRawValue());
    }

    /** Whether this card shows the "needs a short name" error. */
    needsName(row: ResponseRowGroup): boolean {
        return rowNeedsName(row.getRawValue());
    }

    /** Whether this card shows the "needs response text or an image" error. */
    needsContent(row: ResponseRowGroup): boolean {
        return rowNeedsContent(row.getRawValue());
    }

    protected readonly maxResponses = MAX_RESPONSES;
    protected readonly nameMaxLength = RESPONSE_NAME_MAX;
    protected readonly responseMaxLength = RESPONSE_TEXT_MAX;
}
